import {
  ArrowCounterClockwise,
  CheckCircle,
  Clock,
  GitMerge,
  MinusCircle,
  PencilSimple,
  PlusCircle,
  UserPlus,
  XCircle,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import type { ContactActivityEventApi } from '../types/contacts-api.types';
import type { ContactActivityEvent, ContactActivityEventType } from '../types/contact-activity.types';

export type ContactActivityVisual = {
  Icon: PhosphorIcon;
  iconBackground: string;
  iconColor: string;
};

const normalizeActivityType = (type?: string | null): ContactActivityEventType => {
  const normalized = type?.trim().toLowerCase().replace(/[\s-]+/g, '_') ?? '';

  switch (normalized) {
    case 'created':
    case 'contact_created':
      return 'created';
    case 'updated':
    case 'contact_updated':
      return 'updated';
    case 'merged':
    case 'contact_merged':
      return 'merged';
    case 'subscribed':
      return 'subscribed';
    case 'unsubscribed':
      return 'unsubscribed';
    case 'pending':
      return 'pending';
    case 'list_added':
    case 'added_to_list':
      return 'list_added';
    case 'list_removed':
    case 'removed_from_list':
      return 'list_removed';
    case 'restored':
      return 'restored';
    default:
      return 'unknown';
  }
};

export const mapContactActivityEventApi = (
  event: ContactActivityEventApi
): ContactActivityEvent => ({
  itemId: event.itemId?.trim() ?? '',
  type: normalizeActivityType(event.type),
  timestamp: event.timestamp ?? undefined,
  source: event.source ?? undefined,
  sourceClient: event.sourceClient ?? undefined,
  // The backend sends lowercase `email`/`sms`; older builds sent `Email`/`Sms`.
  channel: event.channel?.trim().toLowerCase() || undefined,
  meta: event.meta ?? undefined,
});

const SOURCE_LABELS: Record<string, string> = {
  manual: 'Manual',
  api: 'API',
  import: 'Import',
};

/**
 * "Manual · Jane Doe", "API · crm-client", "Import · contacts.csv". The backend stamps every event
 * with a source and names who made the change in `sourceClient`, so a missing source renders as
 * nothing rather than being guessed as "Manual".
 */
export const formatActivitySourceLabel = (
  source?: string | null,
  sourceClient?: string | null
): string | undefined => {
  const trimmed = source?.trim();
  if (!trimmed) return undefined;

  const primary = SOURCE_LABELS[trimmed.toLowerCase()] ?? trimmed;
  const client = sourceClient?.trim();
  return client ? `${primary} · ${client}` : primary;
};

const CHANNEL_LABELS: Record<string, string> = { email: 'Email', sms: 'SMS' };

export const formatActivityChannel = (channel?: string | null): string => {
  const normalized = channel?.trim().toLowerCase() ?? '';
  return CHANNEL_LABELS[normalized] ?? (channel?.trim() || 'Channel');
};

const CHANGE_FIELD_LABELS: Record<string, string> = {
  email: 'Email',
  phone: 'Phone',
  firstname: 'First name',
  lastname: 'Last name',
  locale: 'Locale',
  emailsubscriptionstatus: 'Email status',
  smssubscriptionstatus: 'SMS status',
};

const ATTRIBUTE_FIELD_PREFIX = 'attributes.';

/** A change's field as a person reads it; a custom field (`Attributes.<key>`) by its label. */
export const formatActivityChangeField = (
  field: string,
  attributeLabels: Record<string, string> = {}
): string => {
  const lower = field.toLowerCase();
  if (lower.startsWith(ATTRIBUTE_FIELD_PREFIX)) {
    const key = field.slice(ATTRIBUTE_FIELD_PREFIX.length);
    return attributeLabels[key] ?? key;
  }
  return CHANGE_FIELD_LABELS[lower] ?? field;
};

/**
 * One line per field an `updated`/`merged` event changed: "First name: Jane → Janet". The
 * backend records each custom field as its own `Attributes.<key>` change with real values.
 */
export const getContactActivityChangeLines = (
  event: ContactActivityEvent,
  attributeLabels: Record<string, string> = {}
): string[] => {
  if (event.type !== 'updated' && event.type !== 'merged') return [];

  return (event.meta?.changes ?? []).flatMap((change) => {
    const field = change.field?.trim();
    if (!field) return [];
    // Events stored before the per-key diff carry one placeholder entry, `Attributes: → changed`,
    // which names no field and no value — nothing a reader can use.
    if (field.toLowerCase() === 'attributes' && change.newValue?.trim() === 'changed') return [];

    const oldValue = change.oldValue?.trim() || '—';
    const newValue = change.newValue?.trim() || '—';
    return [`${formatActivityChangeField(field, attributeLabels)}: ${oldValue} → ${newValue}`];
  });
};

export const getContactActivityVisual = (event: ContactActivityEvent): ContactActivityVisual => {
  switch (event.type) {
    case 'created':
      return { Icon: UserPlus, iconBackground: 'bg-primary-50', iconColor: 'text-primary-600' };
    case 'subscribed':
      return { Icon: CheckCircle, iconBackground: 'bg-emerald-50', iconColor: 'text-emerald-700' };
    case 'unsubscribed':
      return { Icon: XCircle, iconBackground: 'bg-error-background', iconColor: 'text-error' };
    case 'pending':
      return { Icon: Clock, iconBackground: 'bg-amber-50', iconColor: 'text-amber-700' };
    case 'list_removed':
      return { Icon: MinusCircle, iconBackground: 'bg-amber-50', iconColor: 'text-amber-700' };
    case 'list_added':
      return { Icon: PlusCircle, iconBackground: 'bg-primary-50', iconColor: 'text-primary-600' };
    case 'merged':
      return { Icon: GitMerge, iconBackground: 'bg-neutral-100', iconColor: 'text-neutral-600' };
    case 'updated':
      return { Icon: PencilSimple, iconBackground: 'bg-neutral-100', iconColor: 'text-neutral-600' };
    case 'restored':
      return { Icon: ArrowCounterClockwise, iconBackground: 'bg-primary-50', iconColor: 'text-primary-600' };
    default:
      return { Icon: PencilSimple, iconBackground: 'bg-neutral-100', iconColor: 'text-neutral-600' };
  }
};

export const formatContactActivityLabel = (event: ContactActivityEvent): string => {
  switch (event.type) {
    case 'created':
      return 'Contact created';
    case 'updated':
      return 'Contact updated';
    case 'merged':
      return 'Merged with an existing contact';
    case 'restored':
      // A previously deleted contact brought back by a create or import with its address.
      return 'Contact restored';
    case 'subscribed':
      return `${formatActivityChannel(event.channel)} subscribed`;
    case 'unsubscribed':
      return `${formatActivityChannel(event.channel)} unsubscribed`;
    case 'pending':
      return `${formatActivityChannel(event.channel)} awaiting confirmation`;
    case 'list_added': {
      const listName = event.meta?.listName?.trim() || event.meta?.listId?.trim();
      return listName ? `Added to "${listName}"` : 'Added to list';
    }
    case 'list_removed': {
      const listName = event.meta?.listName?.trim() || event.meta?.listId?.trim();
      return listName ? `Removed from "${listName}"` : 'Removed from list';
    }
    default:
      return event.type === 'unknown' ? 'Activity' : event.type.replace(/_/g, ' ');
  }
};
